import React, { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import MicOffIcon from "@mui/icons-material/MicOff";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import {
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  Card,
} from "@mui/material";
import {
  StyledButton,
  StyledInputLabel,
  StyledSelect,
  StyledTextField,
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledDatePicker,
  StyledMultilineTextField,
  OutlinedIconFab,
  StyledTableContainer,
} from "../StyledComponents";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { type } from "jquery";
import { durationType } from "../initialData/initialData";
import { getHumanReadableDiffInPastDateWithAgo } from "../Utils/UtilsFunctions";
import Icon from "../Utils/Icons";
import { useLocation } from "react-router-dom";
import DurationAutocompleteField from "./DurationAutocompleteField";
import {
  parseCombinedDuration,
  formatDurationTextForDisplay,
} from "./durationUtils";
import useSectionFieldLayout from "../config/hooks/useSectionFieldLayout";
import { FIELD_SET_IDS } from "../config/defaults/fieldLayoutUtils";

const MenstrualHistory = ({
  menstrualHistoryData,
  setMenstrualHistoryData,
  dateForAppointment,
  startRecording,
  stopRecording,
  isAiEnabled,
  fieldLayout: fieldLayoutProp,
}) => {
  const fieldLayout = useSectionFieldLayout(
    fieldLayoutProp,
    FIELD_SET_IDS.menstrual,
  );
  console.log("Menstrual History called", menstrualHistoryData);
  const location = useLocation();
  const today = dayjs(dateForAppointment);
  const [menstrualHistoryVars, setMenstrualHistoryVars] = useState({
    description: "",
    date: null,
    menstrualType: "",
    duration: "",
  });
  const [error, setError] = useState("");
  const [listeningOn, setListeningOn] = useState(false);
  const [menstrualHistoryEditIndex, setMenstrualHistoryEditIndex] =
    useState(null);
  const hasPendingDurationSelection = (durationValue) =>
    /^\d+$/.test(String(durationValue || "").trim());
  const handleAddRow = () => {
    if (
      fieldLayout.isFieldVisible("duration", menstrualHistoryVars) &&
      fieldLayout.isFieldRequired("duration", menstrualHistoryVars) &&
      !menstrualHistoryVars.duration
    ) {
      setError(
        fieldLayout.getValidationMessage(
          "duration",
          "Duration is a mandatory field.",
        ),
      );
      return;
    } else if (hasPendingDurationSelection(menstrualHistoryVars.duration)) {
      setError("Please select a duration");
      return;
    }
    if (
      menstrualHistoryVars.description &&
      menstrualHistoryVars.menstrualType
    ) {
      // Store the updated state in a local variable
      const newRow = {
        description: menstrualHistoryVars.description,
        date: menstrualHistoryVars?.date?.format("YYYY-MM-DD"),
        menstrualType: menstrualHistoryVars?.menstrualType,
        duration: menstrualHistoryVars?.duration,
      };

      // Update menstrual history rows by adding the new row

      const updatedRows = [...menstrualHistoryData["new"], newRow];

      setMenstrualHistoryData({
        ...menstrualHistoryData,
        new: updatedRows,
      });
      // Reset menstrual history variables and clear error
      setMenstrualHistoryVars({
        description: "",
        date: null,
        menstrualType: "",
        duration: "",
      });
      setError("");
    } else {
      // Set error message if any field is missing
      setError("Type and Description fields are required");
    }
  };
  const handleMenstrualHistoryEditClick = (row, rowIndex) => {
    const normalizedDuration = row?.durationType
      ? `${row?.duration || ""} ${row?.durationType}`.trim()
      : row?.duration || "";
    setMenstrualHistoryVars({
      description: row.description,
      date: row.date ? dayjs(row.date) : null,
      menstrualType: row?.menstrualType,
      duration: normalizedDuration,
    });
    setMenstrualHistoryEditIndex(rowIndex);
  };
  const handleMenstrualHistoryUpdate = () => {
    if (
      fieldLayout.isFieldVisible("duration", menstrualHistoryVars) &&
      fieldLayout.isFieldRequired("duration", menstrualHistoryVars) &&
      !menstrualHistoryVars.duration
    ) {
      setError(
        fieldLayout.getValidationMessage(
          "duration",
          "Duration is a mandatory field.",
        ),
      );
      return;
    } else if (hasPendingDurationSelection(menstrualHistoryVars.duration)) {
      setError("Please select a duration");
      return;
    }
    if (
      menstrualHistoryVars.description &&
      menstrualHistoryVars.menstrualType
    ) {
      // Store the updated state in a local variable
      const updatedRow = {
        description: menstrualHistoryVars.description,
        date: menstrualHistoryVars?.date?.format("YYYY-MM-DD"),
        menstrualType: menstrualHistoryVars?.menstrualType,
        duration: menstrualHistoryVars?.duration,
      };

      // Update menstrual history rows by adding the new row

      const updatedMenstrualHistory = menstrualHistoryData["new"].map(
        (row, index) => {
          if (index === menstrualHistoryEditIndex) {
            return updatedRow;
          } else {
            return row;
          }
        }
      );
      setMenstrualHistoryData({
        ...menstrualHistoryData,
        new: updatedMenstrualHistory,
      });
      // Reset menstrual history variables and clear error
      setMenstrualHistoryVars({
        description: "",
        date: null,
        menstrualType: "",
        duration: "",
      });
      setMenstrualHistoryEditIndex(null);
      setError("");
    } else {
      // Set error message if any field is missing
      setError("Type and Description fields are required");
    }
  };
  const handleMenstrualHistoryDelete = (rowIndex) => {
    // Filter out the row at the specified index
    const updatedMenstrualHistory = menstrualHistoryData["new"].filter(
      (row, index) => index !== rowIndex
    );
    // Update the state with the filtered array
    setMenstrualHistoryData({
      ...menstrualHistoryData,
      new: updatedMenstrualHistory,
    });
  };
  const handleChangeMenstrualHistory = (field, value) => {
    let updatedDetails = { ...menstrualHistoryVars, [field]: value };

    if (field === "duration") {
      const { value: durationValue, unit: durationUnit } = parseCombinedDuration(
        updatedDetails.duration
      );
      if (durationValue && durationUnit) {
        const calculatedDate = calculateOnsetDate(
          parseInt(durationValue, 10),
          durationUnit
        );
        updatedDetails.date = calculatedDate ?? null;
      } else {
        updatedDetails.date = null;
      }
    } else if (field === "durationType") {
      updatedDetails.date = null;
    }

    setMenstrualHistoryVars(updatedDetails);
  };
  const calculateOnsetDate = (duration, type) => {
    if (!duration) return null;
    const today = dayjs(dateForAppointment); // appointment date
    const durationMap = {
      minute: "minute",
      minutes: "minute",
      hour: "hour",
      hours: "hour",
      day: "day",
      days: "day",
      week: "week",
      weeks: "week",
      month: "month",
      months: "month",
      year: "year",
      years: "year",
    };
    console.log("date onset", today.subtract(duration, durationMap[type]));
    return today.subtract(duration, durationMap[type]);
  };
  const handleClearMenstrualDescription = () => {
    setMenstrualHistoryVars({
      ...menstrualHistoryVars,
      description: "", // Use computed property name to set the field dynamically
    });
  };

  const renderMenstrualTableCell = (column, row, { isPrev = false } = {}) => {
    switch (column.key) {
      case "menstrualType":
        return row?.menstrualType ?? "-";
      case "description":
        return row?.description ?? "-";
      case "duration":
        if (isPrev) {
          return row?.date
            ? formatDurationTextForDisplay(
                getHumanReadableDiffInPastDateWithAgo(row?.date, row),
              )
            : "-";
        }
        return row?.duration
          ? formatDurationTextForDisplay(row.duration)
          : row?.durationType
            ? formatDurationTextForDisplay(
                `${row?.duration || ""} ${row?.durationType}`.trim(),
              )
            : "-";
      default:
        return row?.[column.key] ?? "-";
    }
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        boxShadow: "0 4px 4px 0 #C7C7CC inset",
        backgroundColor: "#FFF",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          margin: "5px 0px",
          display: "flex",
          alignItems: "center",
          //   alignContent: "center",
        }}
      >
        {fieldLayout.isFieldVisible("menstrualType", menstrualHistoryVars) && (
        <Grid item {...fieldLayout.getFieldGrid("menstrualType", { xs: 11, sm: 4 })} sx={{ margin: "5px 0px" }}>
          <StyledTextField
            select
            value={menstrualHistoryVars.menstrualType}
            onChange={(e) =>
              handleChangeMenstrualHistory("menstrualType", e.target.value)
            }
            label={fieldLayout.getFieldLabel("menstrualType", "Select Type", menstrualHistoryVars)}
            fullWidth
          >
            {[
              "Age of menarche",
              "Last menstrual period",
              "Age of menopause",
              "Number of menstrual periods per year",
              "Ovulation date",
              "Length of menstrual cycle",
              "Menstrual flow intensity",
              "Menstrual pain intensity (Dysmenorrhea)",
            ].map((val) => (
              <MenuItem key={val} value={val} sx={{ fontSize: "12px" }}>
                {val}
              </MenuItem>
            ))}
          </StyledTextField>
        </Grid>
        )}
        {fieldLayout.isFieldVisible("description", menstrualHistoryVars) && (
        <Grid item {...fieldLayout.getFieldGrid("description", { xs: 11, sm: 7 })} sx={{ margin: "5px 0px" }}>
          <StyledTextField
            fullWidth
            label={fieldLayout.getFieldLabel("description", "Description", menstrualHistoryVars)}
            value={menstrualHistoryVars.description}
            onChange={(e) =>
              handleChangeMenstrualHistory("description", e.target.value)
            }
            InputProps={{
              endAdornment: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>
                    <Tooltip title="Clear">
                      <OutlinedIconFab
                        size="small"
                        variant="contained"
                        onClick={handleClearMenstrualDescription}
                      >
                        <Icon name="clearTextField" size={32} />
                      </OutlinedIconFab>
                    </Tooltip>
                  </div>

                  <>
                    {isAiEnabled &&
                      location.pathname.includes("DoctorConsole") &&
                      (listeningOn == false ? (
                        <Tooltip title="Start Listening">
                          <OutlinedIconFab
                            // onClick={startListening}
                            onClick={() => {
                              startRecording();
                              setListeningOn(true);
                            }}
                            variant="contained"
                            color="defaultColor"
                            size="small"
                          >
                            <Icon name="micNotInUse" size={32} />
                          </OutlinedIconFab>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Stop Listening">
                          <OutlinedIconFab
                            onClick={() => {
                              // SpeechRecognition.stopListening();
                              stopRecording("Menstrual History");
                              setListeningOn(false);
                            }}
                            variant="outlined"
                            color="defaultColor"
                            size="small"
                          >
                            <Icon name="micInUse" size={32} />
                          </OutlinedIconFab>
                        </Tooltip>
                      ))}
                  </>
                </div>
              ),
            }}
          />
        </Grid>
        )}

        {fieldLayout.isFieldVisible("duration", menstrualHistoryVars) && (
        <Grid item {...fieldLayout.getFieldGrid("duration", { xs: 11, sm: 11, md: 8 })} style={{ display: "flex" }}>
          <span
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {fieldLayout.getFieldLabel("duration", "Time Since Observation:", menstrualHistoryVars)}
          </span>
          <DurationAutocompleteField
            label="Duration"
            value={menstrualHistoryVars.duration || ""}
            onChange={(combined) =>
              handleChangeMenstrualHistory("duration", combined)
            }
            durationType={durationType}
          />
        </Grid>
        )}

        <Grid item xs={11} md={4} sx={{ margin: "5px 0px", display: "none" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <StyledDatePicker
                label="Observation Date"
                onChange={(date) => handleChangeMenstrualHistory("date", date)}
                slotProps={{
                  field: { clearable: true },
                }}
                maxDate={today}
                value={
                  menstrualHistoryVars?.date
                    ? dayjs(menstrualHistoryVars?.date)
                    : null
                }
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>

        <Grid
          item
          xs={11}
          sm={3}
          sx={{
            margin: "5px 0px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <StyledButton
            bgColor="#0E3F66"
            variant="contained"
            fullWidth
            onClick={
              menstrualHistoryEditIndex !== null
                ? handleMenstrualHistoryUpdate
                : handleAddRow
            }
            color="defaultColor"
          >
            {menstrualHistoryEditIndex !== null ? "Update" : "Add"}
          </StyledButton>
        </Grid>
        <Grid
          item
          xs={11}
          sm={11}
          sx={{
            margin: "5px 0px",
          }}
        >
          {error && <Typography style={{ color: "red" }}>{error}</Typography>}
        </Grid>

        <Grid item xs={11} sm={11}>
          {menstrualHistoryData["prev"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   marginTop: "20px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    {fieldLayout.tableColumns.map((column) => (
                      <StyledTableCellHistory key={column.key}>
                        {column.label}
                      </StyledTableCellHistory>
                    ))}
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {menstrualHistoryData["prev"].map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      {fieldLayout.tableColumns.map((column) => (
                        <StyledTableCellHistory key={column.key}>
                          {renderMenstrualTableCell(column, row, { isPrev: true })}
                        </StyledTableCellHistory>
                      ))}
                      <StyledTableCellHistory>-</StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
        <Grid item xs={11} sm={11}>
          {menstrualHistoryData["new"].length > 0 && (
            <StyledTableContainer
              component={Paper}
            // sx={{
            //   margin: "5px 2px",
            //   border: "1px solid #21cdc0",
            // }}
            >
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    {fieldLayout.tableColumns.map((column) => (
                      <StyledTableCellHistory key={column.key}>
                        {column.label}
                      </StyledTableCellHistory>
                    ))}
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {menstrualHistoryData["new"].map((row, rowIndex) => (
                    <StyledTableRowHistory key={rowIndex}>
                      {fieldLayout.tableColumns.map((column) => (
                        <StyledTableCellHistory key={column.key}>
                          {renderMenstrualTableCell(column, row, { isPrev: false })}
                        </StyledTableCellHistory>
                      ))}

                      <StyledTableCellHistory>
                        {menstrualHistoryData["prev"].includes(row) ? (
                          "-"
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            <Tooltip title="Edit">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleMenstrualHistoryEditClick(row, rowIndex)
                                }
                                sx={{ marginRight: 1 }}
                              >
                                <Icon name="editInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <OutlinedIconFab
                                onClick={() =>
                                  handleMenstrualHistoryDelete(rowIndex)
                                }
                              >
                                <Icon name="deleteInTable" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          </div>
                        )}
                      </StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default MenstrualHistory;
